import { API_BASE_URL } from '../config';
import React from 'react';
import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import RoomCalendar from '../components/RoomCalendar';

const TherapistDashboard = () => {
  // Active section state
  const [activeSection, setActiveSection] = useState(null);

  // User permissions state
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const [procedurePlans, setProcedurePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper function to check if user has permission for a gadget
  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  // Function to fetch user permissions
  const fetchUserPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissionsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const permissions = await response.json();
        setUserPermissions(permissions);
        console.log('Therapist Dashboard - User permissions:', permissions);
      } else {
        console.error('Failed to fetch user permissions');
        setUserPermissions({});
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setUserPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  const fetchProcedurePlans = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    let therapistId;
    let userRole;
    try {
      const decodedToken = jwtDecode(token);
      therapistId = decodedToken._id;
      userRole = decodedToken.roleId?.ROLE_NAME || 'Therapist';
    } catch (err) {
      setError('Failed to decode token. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/procedurePlans/therapist/${therapistId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const filteredPlans = (Array.isArray(data) ? data : []).filter(plan => {
          const procedureDate = new Date(plan.PROCEDURE_DATE);
          procedureDate.setHours(0, 0, 0, 0);
          return (plan.PROCEDURE_STATUS === 'Planned' || plan.PROCEDURE_STATUS === 'Scheduled') && 
                 procedureDate >= now;
        });
        setProcedurePlans(filteredPlans);
      } else {
        setError(data.message || 'Failed to fetch procedure plans.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (planId, newStatus) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication token not found. Please log in again.');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/procedurePlans/${planId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`Procedure status updated to ${newStatus} successfully!`);
        fetchProcedurePlans(); // Refresh the list
      } else {
        alert(data.message || 'Failed to update procedure status.');
      }
    } catch (err) {
      alert(`Network error: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
    fetchProcedurePlans();
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>
      <h2>Therapist Dashboard</h2>
      
      {/* Dashboard Gadgets/Buttons */}
      {permissionsLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          Loading dashboard permissions...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        
        {/* Upcoming Procedures Gadget */}
        {hasPermission('Procedure Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📋 Upcoming Procedures</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              View and manage upcoming procedure plans
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'upcomingProcedures' ? null : 'upcomingProcedures')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'upcomingProcedures' ? '#dc3545' : '#ffc107',
                color: activeSection === 'upcomingProcedures' ? 'white' : '#212529',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'upcomingProcedures' ? 'Close Procedures' : 'View Procedures'}
            </button>
          </div>
        )}

        {/* Procedure Management Gadget */}
        {hasPermission('Procedure Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>⚕️ Procedure Management</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Update procedure status and manage workflow
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'procedureManagement' ? null : 'procedureManagement')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'procedureManagement' ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'procedureManagement' ? 'Close Management' : 'Manage Procedures'}
            </button>
          </div>
        )}

        {hasPermission('Room Calendar') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🚪 Room calendar</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              See which room is booked and the room details
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'roomCalendar' ? null : 'roomCalendar')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'roomCalendar' ? '#dc3545' : '#0f766e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'roomCalendar' ? 'Close rooms' : 'View rooms'}
            </button>
          </div>
        )}
        
        {/* No gadgets available message */}
        {!permissionsLoading && !hasPermission('Procedure Management') && !hasPermission('Room Calendar') && (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}>
            <h3>No Dashboard Access</h3>
            <p>You don't have permission to access any dashboard gadgets. Please contact your administrator.</p>
          </div>
        )}
      </div>
      )}

      {/* Conditional Sections */}
      {activeSection === 'roomCalendar' && hasPermission('Room Calendar') && (
        <RoomCalendar />
      )}

      {activeSection === 'upcomingProcedures' && hasPermission('Procedure Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Upcoming Procedures</h3>
          
          {loading && <p>Loading procedure plans...</p>}
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}

          {!loading && !error && procedurePlans.length === 0 && (
            <p>No upcoming procedure plans found.</p>
          )}

          {!loading && !error && procedurePlans.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Patient</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Procedure</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Time</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Therapist</th>
                  </tr>
                </thead>
                <tbody>
                  {procedurePlans.map((plan) => (
                    <tr key={plan._id}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {plan.APPOINTMENT_ID?.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {plan.PROCEDURE_ID?.PROCEDURE_NAME || 'Unknown Procedure'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {new Date(plan.PROCEDURE_DATE).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {plan.PROCEDURE_TIME || 'Not specified'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          backgroundColor: plan.PROCEDURE_STATUS === 'Scheduled' ? '#d4edda' : '#fff3cd',
                          color: plan.PROCEDURE_STATUS === 'Scheduled' ? '#155724' : '#856404'
                        }}>
                          {plan.PROCEDURE_STATUS}
                        </span>
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {plan.THERAPIST_ID?.firstName || 'Unknown Therapist'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'procedureManagement' && hasPermission('Procedure Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Procedure Management</h3>
          
          {loading && <p>Loading procedure plans...</p>}
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}

          {!loading && !error && procedurePlans.length === 0 && (
            <p>No procedures available for management.</p>
          )}

          {!loading && !error && procedurePlans.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Patient</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Procedure</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Current Status</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Update Status</th>
                  </tr>
                </thead>
                <tbody>
                  {procedurePlans.map((plan) => (
                    <tr key={plan._id}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {plan.APPOINTMENT_ID?.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {plan.PROCEDURE_ID?.PROCEDURE_NAME || 'Unknown Procedure'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {new Date(plan.PROCEDURE_DATE).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          backgroundColor: plan.PROCEDURE_STATUS === 'Scheduled' ? '#d4edda' : '#fff3cd',
                          color: plan.PROCEDURE_STATUS === 'Scheduled' ? '#155724' : '#856404'
                        }}>
                          {plan.PROCEDURE_STATUS}
                        </span>
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {plan.PROCEDURE_STATUS === 'Planned' && (
                            <button
                              onClick={() => handleStatusUpdate(plan._id, 'Scheduled')}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.7rem'
                              }}
                            >
                              Mark Scheduled
                            </button>
                          )}
                          {plan.PROCEDURE_STATUS === 'Scheduled' && (
                            <button
                              onClick={() => handleStatusUpdate(plan._id, 'In Progress')}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.7rem'
                              }}
                            >
                              Start Procedure
                            </button>
                          )}
                          <button
                            onClick={() => handleStatusUpdate(plan._id, 'Completed')}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.7rem'
                            }}
                          >
                            Mark Complete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TherapistDashboard;